import { z } from "zod";

import {
  DateTimeString,
  getDateTimeString,
  zDateTimeString,
} from "../../dateTypes.js";
import { CellExecutionState } from "../../enums.js";
import { AppSessionCellId } from "../../idTypeBrands.js";
import { typedObjectEntries } from "../../utils/typedObjects.js";
import { zAppSessionCellId } from "../../zod/ZodIdTypeBrands.js";
import { createAppSessionEventDefinition } from "../AppSessionEventDefinition.js";

export const UPDATE_APP_SESSION_CELL = createAppSessionEventDefinition({
  type: "UPDATE_APP_SESSION_CELL",
  payloadSchema: z.object({
    appSessionCellId: zAppSessionCellId,
    state: z.nativeEnum(CellExecutionState).optional(),
    lastExecutionEndTime: zDateTimeString.nullish(),
    lastExecutionStartTime: zDateTimeString.nullish(),
    lastExecutionTraceId: z.string().nullish(),
    lastRunSourceRevision: z.number().nullish(),
    lastRunStateRevision: z.number().nullish(),
    lastRunExecutionRevision: z.number().nullish(),
  }),
  logSafe: {
    appSessionCellId: true,
    state: true,
    lastExecutionEndTime: true,
    lastExecutionStartTime: true,
    lastExecutionTraceId: true,
    lastRunSourceRevision: true,
    lastRunStateRevision: true,
    lastRunExecutionRevision: true,
  },
  create: ({
    lastExecutionEndTime,
    lastExecutionStartTime,
    ...rest
  }: {
    appSessionCellId: AppSessionCellId;
    state?: CellExecutionState;
    lastExecutionEndTime?: Date | DateTimeString | null;
    lastExecutionStartTime?: Date | DateTimeString | null;
    lastExecutionTraceId?: string | null;
    lastRunSourceRevision?: number | null;
    lastRunStateRevision?: number | null;
    lastRunExecutionRevision?: number | null;
  }) => ({
    lastExecutionEndTime:
      lastExecutionEndTime instanceof Date
        ? getDateTimeString(lastExecutionEndTime)
        : lastExecutionEndTime,
    lastExecutionStartTime:
      lastExecutionStartTime instanceof Date
        ? getDateTimeString(lastExecutionStartTime)
        : lastExecutionStartTime,
    ...rest,
  }),
  apply: ({ payload: { appSessionCellId, ...payload }, state }) => {
    const warnings: string[] = [];
    const appSessionCell = state.appSessionCells[appSessionCellId];
    if (appSessionCell == null) {
      warnings.push(`App session cell not found: ${appSessionCellId}`);
    } else {
      for (const entry of typedObjectEntries(payload)) {
        if (entry != null) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we know that this key must match
          (appSessionCell[entry[0]] as any) = entry[1] as any;
        }
      }
    }
    return { warnings };
  },
});
